/* VORAIR motion + clip controller. No dependencies. */
(function () {
  'use strict';

  var root = document.documentElement;
  var mq = function (q) { return window.matchMedia ? window.matchMedia(q) : { matches: false, addEventListener: function () {} }; };
  var reduce = mq('(prefers-reduced-motion: reduce)').matches;
  var wide = mq('(min-width: 750px)');
  var hasIO = 'IntersectionObserver' in window;

  /* ---------- scroll reveal ---------- */
  var revealIO = null;
  if (root.classList.contains('vor-motion') && hasIO) {
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('vor-in');
        revealIO.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  }

  function scanReveals(scope) {
    if (!revealIO) return;
    (scope || document).querySelectorAll('.vor-reveal:not(.vor-in), .vor-stagger:not(.vor-in)').forEach(function (node) {
      revealIO.observe(node);
    });
  }

  /* ---------- clips ----------
     Short muted loops. Only one video plays at a time on the whole page.
     A group marked [data-vor-tour] (the How it works steps) plays its clips
     in order on wide screens, highlighting the active step. Everywhere else
     the single most-visible clip plays. Reduced motion: nothing autoplays. */
  var DWELL = 3200;
  var clips = [];
  var tours = [];
  var byRatio = function (a, b) { return b.ratio - a.ratio; };

  function setState(clip, state) {
    clip.el.setAttribute('data-state', state);
    if (clip.btn) clip.btn.setAttribute('aria-label', state === 'playing' ? 'Pause video' : 'Play video');
  }

  function play(clip) {
    if (!clip || clip.userPaused) return;
    var v = clip.video;
    if (v.getAttribute('preload') === 'none') v.setAttribute('preload', 'auto');
    var p = v.play();
    if (p && typeof p.catch === 'function') p.catch(function (err) {
      /* NotAllowedError = the browser blocked autoplay. Surface the play button
         instead of leaving a still frame with no way to start it. */
      if (err && err.name === 'NotAllowedError') setState(clip, 'blocked');
    });
    setState(clip, 'playing');
  }

  function pause(clip) {
    if (!clip) return;
    clip.video.pause();
    setState(clip, 'paused');
  }

  function cardOf(clip) {
    return clip.el.closest ? clip.el.closest('.vor-step') : null;
  }

  function stopTour(t) {
    clearTimeout(t.timer);
    t.running = false;
    t.items.forEach(function (c) {
      var card = cardOf(c);
      if (card) card.classList.remove('is-active');
    });
  }

  function stopTours(except) {
    tours.forEach(function (t) { if (t !== except) stopTour(t); });
  }

  function tourStep(t) {
    if (!t.running) return;
    var clip = t.items[t.index % t.items.length];
    t.items.forEach(function (c) {
      if (c === clip) return;
      pause(c);
      var other = cardOf(c);
      if (other) other.classList.remove('is-active');
    });
    var card = cardOf(clip);
    if (card) {
      card.style.setProperty('--vor-dwell', DWELL + 'ms');
      card.classList.remove('is-active');
      void card.offsetWidth;
      card.classList.add('is-active');
    }
    try { clip.video.currentTime = 0; } catch (e) {}
    play(clip);
    t.timer = setTimeout(function () {
      t.index = (t.index + 1) % t.items.length;
      tourStep(t);
    }, DWELL);
  }

  function decide() {
    if (reduce || document.hidden) {
      stopTours();
      clips.forEach(pause);
      return;
    }

    /* a clip the visitor started by hand keeps the floor while it is visible */
    clips.forEach(function (c) { if (c.ratio < 0.2) c.manual = false; });
    var manual = clips.filter(function (c) { return c.manual && !c.userPaused; })[0];
    if (manual) {
      stopTours();
      clips.forEach(function (c) { if (c !== manual) pause(c); });
      return;
    }

    var tour = null;
    if (wide.matches) {
      tour = tours.filter(function (t) {
        return t.ratio >= 0.35 && t.items.some(function (c) { return !c.userPaused; });
      }).sort(byRatio)[0] || null;
    }
    if (tour) {
      stopTours(tour);
      clips.forEach(function (c) { if (c.tour !== tour) pause(c); });
      if (!tour.running) {
        tour.running = true;
        tourStep(tour);
      }
      return;
    }

    stopTours();
    var best = clips.filter(function (c) { return c.ratio >= 0.6 && !c.userPaused; }).sort(byRatio)[0];
    clips.forEach(function (c) { if (c !== best) pause(c); });
    if (best) play(best);
  }

  var clipIO = hasIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
      if (entry.target.__vorTour) entry.target.__vorTour.ratio = ratio;
      if (entry.target.__vorClip) {
        /* A clip taller than the screen can never be 60% visible, so it would
           never start. Also count it as on screen when it fills most of the
           viewport. */
        var vh = entry.rootBounds && entry.rootBounds.height;
        if (ratio && vh) ratio = Math.max(ratio, entry.intersectionRect.height / vh);
        entry.target.__vorClip.ratio = ratio;
      }
    });
    decide();
  }, { threshold: [0, 0.2, 0.35, 0.6, 0.8, 1] }) : null;

  function initClips(scope) {
    (scope || document).querySelectorAll('[data-vor-clip]').forEach(function (el) {
      if (el.__vorClip) return;
      var video = el.querySelector('video');
      if (!video) return;
      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.loop = true;
      video.removeAttribute('autoplay');
      video.removeAttribute('controls');

      var clip = { el: el, video: video, btn: el.querySelector('[data-vor-clip-toggle]'), ratio: 0, userPaused: reduce, manual: false, tour: null };
      el.__vorClip = clip;
      clips.push(clip);
      setState(clip, 'paused');

      if (clip.btn) {
        clip.btn.addEventListener('click', function () {
          if (el.getAttribute('data-state') === 'playing') {
            clip.userPaused = true;
            clip.manual = false;
            pause(clip);
            if (clip.tour) stopTour(clip.tour);
          } else {
            clip.userPaused = false;
            clip.manual = true;
            stopTours();
            clips.forEach(function (c) { if (c !== clip) pause(c); });
            play(clip);
          }
        });
      }
      if (clipIO) clipIO.observe(el);
    });

    (scope || document).querySelectorAll('[data-vor-tour]').forEach(function (el) {
      if (el.__vorTour) return;
      var t = { el: el, items: [], index: 0, timer: null, ratio: 0, running: false };
      el.__vorTour = t;
      el.querySelectorAll('[data-vor-clip]').forEach(function (node) {
        if (node.__vorClip) {
          node.__vorClip.tour = t;
          t.items.push(node.__vorClip);
        }
      });
      if (t.items.length) {
        tours.push(t);
        if (clipIO) clipIO.observe(el);
      }
    });
  }

  scanReveals();
  initClips();

  document.addEventListener('visibilitychange', decide);
  if (wide.addEventListener) wide.addEventListener('change', decide);

  /* Theme editor: reveal re-rendered sections immediately and pick up new clips. */
  document.addEventListener('shopify:section:load', function (event) {
    if (window.Shopify && window.Shopify.designMode) {
      event.target.querySelectorAll('.vor-reveal, .vor-stagger').forEach(function (node) { node.classList.add('vor-in'); });
    } else {
      scanReveals(event.target);
    }
    initClips(event.target);
  });
})();
